import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import {
  PackageCheck,
  TrendingUp,
  AlertTriangle,
  ListFilter,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  ExternalLink,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronsUpDown,
  Check,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { api, type TdsynnexProduct, type CuratedState, type BrandOption } from "@/lib/api";
import { useShopifyAdminURL } from "@/lib/useShopifyAdmin";
import { formatInt, formatUsd, truncate } from "@/lib/utils";
import { cn } from "@/lib/utils";

// ── UNSPSC prefix → human label ───────────────────────────────────────────
const UNSPSC_CATEGORY: Record<string, string> = {
  "4321": "Computers",
  "4322": "Network Equipment",
  "4323": "Software",
  "4318": "Storage",
  "4319": "Printers & Imaging",
  "4320": "AV",
  "4316": "Phones",
  "4315": "Security",
  "4314": "Accessories",
  "4313": "Servers",
  "4317": "POS / Retail",
  "2612": "Cables",
  "2411": "Power/UPS",
  "2410": "Racks",
  "2611": "Batteries",
  "8111": "IT Services",
  "8112": "SW Services",
  "4614": "Office Supplies",
};

function inferCategory(unspsc: string | null): string {
  if (!unspsc || unspsc.length < 4) return "Other";
  return UNSPSC_CATEGORY[unspsc.slice(0, 4)] ?? "Other";
}

// ── Preset definitions ─────────────────────────────────────────────────────
type Preset = "profitable" | "low_inventory" | "ready_to_list";

const PRESETS: { id: Preset; label: string; icon: React.ReactNode; desc: string }[] =
  [
    {
      id: "profitable",
      label: "≥12% margin",
      icon: <TrendingUp className="h-3.5 w-3.5" />,
      desc: "MAP − cost ÷ MAP ≥ 12% — your sweet-spot SKUs",
    },
    {
      id: "ready_to_list",
      label: "Ready to list",
      icon: <PackageCheck className="h-3.5 w-3.5" />,
      desc: "In-stock + has MAP + has description",
    },
    {
      id: "low_inventory",
      label: "Low inventory",
      icon: <AlertTriangle className="h-3.5 w-3.5" />,
      desc: "1–5 units remaining — act before they zero out",
    },
  ];

// ── Brand multi-select dropdown ───────────────────────────────────────────
function BrandCombobox({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data } = useQuery({
    queryKey: ["brands", search],
    queryFn: () => api.brands(search || undefined),
    staleTime: 30_000,
  });

  const options: BrandOption[] = data?.items ?? [];

  const toggle = (slug: string) => {
    onChange(value.includes(slug) ? value.filter((v) => v !== slug) : [...value, slug]);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Focus search input when opening
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const label =
    value.length === 0
      ? "All brands"
      : value.length === 1
        ? value[0]
        : `${value[0]} +${value.length - 1}`;

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Trigger */}
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        className={cn(
          "h-6 w-full flex items-center justify-between gap-1 rounded border border-border/40",
          "bg-background/60 px-1.5 text-xs text-foreground",
          "hover:border-border focus:outline-none focus:ring-1 focus:ring-ring",
          value.length > 0 && "border-emerald-500/50",
        )}
      >
        <span className={value.length > 0 ? "text-foreground" : "text-muted-foreground truncate"}>
          {label}
        </span>
        <span className="flex items-center gap-0.5 shrink-0">
          {value.length > 0 && (
            <X
              className="h-3 w-3 text-muted-foreground hover:text-foreground"
              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onChange([]); }}
            />
          )}
          <ChevronsUpDown className="h-3 w-3 opacity-50" />
        </span>
      </button>

      {/* Dropdown — rendered in-flow but fixed-positioned */}
      {open && (
        <div
          className={cn(
            "fixed z-[9999] mt-1 w-52 rounded-md border border-border bg-card shadow-xl",
            "flex flex-col overflow-hidden",
          )}
          style={{
            top: (containerRef.current?.getBoundingClientRect().bottom ?? 0) + 4,
            left: containerRef.current?.getBoundingClientRect().left ?? 0,
          }}
        >
          {/* Search */}
          <div className="flex items-center gap-1.5 border-b px-2 py-1.5">
            <ListFilter className="h-3 w-3 text-muted-foreground shrink-0" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search brand…"
              className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
            />
          </div>

          {/* Options */}
          <div className="max-h-56 overflow-y-auto py-1">
            {options.length === 0 && (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">No brands found</div>
            )}
            {options.map((b) => {
              const selected = value.includes(b.slug);
              return (
                <button
                  key={b.slug}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); toggle(b.slug); }}
                  className="w-full flex items-center gap-2 px-2 py-1 text-xs hover:bg-accent hover:text-accent-foreground"
                >
                  <div className={cn(
                    "h-3.5 w-3.5 rounded-sm border shrink-0 flex items-center justify-center",
                    selected ? "bg-emerald-500 border-emerald-500" : "border-border/60",
                  )}>
                    {selected && <Check className="h-2.5 w-2.5 text-white" />}
                  </div>
                  <span className="flex-1 text-left">{b.slug}</span>
                  <span className="text-[10px] text-muted-foreground tabular-nums">{b.in_stock.toLocaleString()}</span>
                </button>
              );
            })}
          </div>

          {/* Footer */}
          {value.length > 0 && (
            <div className="border-t px-2 py-1.5 flex justify-between items-center">
              <span className="text-[10px] text-muted-foreground">{value.length} selected</span>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onChange([]); }}
                className="text-[10px] text-muted-foreground hover:text-foreground"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const UNSPSC_OPTIONS = Object.entries(UNSPSC_CATEGORY).map(([k, v]) => ({
  prefix: k,
  label: v,
}));

// Sort key used by Go backend
type SortKey =
  | "qty_desc"
  | "qty_asc"
  | "margin_desc"
  | "margin_asc"
  | "cost_asc"
  | "cost_desc"
  | "map_asc"
  | "map_desc"
  | "sku_asc";

// Columns that support sorting: columnId → [asc key, desc key]
const SORT_MAP: Record<string, [SortKey, SortKey]> = {
  sku: ["sku_asc", "sku_asc"], // only asc available for sku
  cost: ["cost_asc", "cost_desc"],
  map: ["map_asc", "map_desc"],
  margin: ["margin_asc", "margin_desc"],
  qty: ["qty_asc", "qty_desc"],
};

// Column filter state shape
interface ColFilters {
  sku_q: string;
  mpn_q: string;
  desc_q: string;
  brand: string[];
  unspsc_prefix: string;
  curated_state: string;
  min_cost: string;
  max_cost: string;
  min_map: string;
  max_map: string;
  min_margin: string;
  max_margin: string;
  min_qty: string;
  max_qty: string;
}

const EMPTY_FILTERS: ColFilters = {
  sku_q: "",
  mpn_q: "",
  desc_q: "",
  brand: [],
  unspsc_prefix: "",
  curated_state: "",
  min_cost: "",
  max_cost: "",
  min_map: "",
  max_map: "",
  min_margin: "",
  max_margin: "",
  min_qty: "",
  max_qty: "",
};

const PAGE_SIZE = 50;

// ── Tiny debounce hook ─────────────────────────────────────────────────────
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── Curated state badge + action cell ─────────────────────────────────────
function CuratedCell({
  product,
  onStateChange,
}: {
  product: TdsynnexProduct;
  onStateChange: (id: string, state: CuratedState) => void;
}) {
  const { curated_id, curated_state, shopify_product_id, qty_on_hand } = product;
  const shopifyAdminBase = useShopifyAdminURL();

  if (!curated_id || !curated_state) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground/40 text-xs">—</span>
        <Button
          asChild
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs opacity-50 hover:opacity-100"
        >
          <Link to={`/curate/${encodeURIComponent(product.tdsynnex_sku)}`}>
            Curate
          </Link>
        </Button>
      </div>
    );
  }

  switch (curated_state) {
    case "draft":
      return (
        <div className="flex items-center gap-1.5">
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px] py-0 px-1.5">
            draft
          </Badge>
          <Button asChild size="sm" variant="ghost" className="h-6 px-2 text-xs">
            <Link to={`/curated/${curated_id}`}>Edit</Link>
          </Button>
        </div>
      );

    case "ready":
      return (
        <div className="flex items-center gap-1.5">
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px] py-0 px-1.5">
            ready
          </Badge>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs text-amber-400 hover:text-amber-300"
            onClick={() => onStateChange(curated_id, "published")}
          >
            Publish
          </Button>
        </div>
      );

    case "published":
      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px] py-0 px-1.5">
            published
          </Badge>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs text-rose-400 hover:text-rose-300"
            onClick={() => onStateChange(curated_id, "archived")}
          >
            Delist
          </Button>
          {shopify_product_id && (
            <a
              href={shopifyAdminBase ? `${shopifyAdminBase}/products/${shopify_product_id}` : "#"}
              target="_blank"
              rel="noreferrer"
              className="text-muted-foreground/60 hover:text-muted-foreground"
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      );

    case "archived": {
      const restocked = qty_on_hand > 0;
      return (
        <div className="flex items-center gap-1.5">
          <Badge
            className={cn(
              "text-[10px] py-0 px-1.5",
              restocked
                ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                : "bg-muted/40 text-muted-foreground border-border/40",
            )}
          >
            archived
          </Badge>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs"
            onClick={() => onStateChange(curated_id, "draft")}
          >
            Re-enlist
          </Button>
        </div>
      );
    }
  }
}

// ── Pagination controls (reused top + bottom) ──────────────────────────────
function PaginationBar({
  page,
  totalPages,
  total,
  pageSize,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPage: (p: number) => void;
}) {
  const [inputVal, setInputVal] = useState(String(page + 1));
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep input in sync when page changes externally (prev/next clicks)
  useEffect(() => {
    setInputVal(String(page + 1));
  }, [page]);

  const rowStart = page * pageSize + 1;
  const rowEnd = Math.min((page + 1) * pageSize, total);

  function commitInput() {
    const n = parseInt(inputVal, 10);
    if (!isNaN(n) && n >= 1 && n <= totalPages) {
      onPage(n - 1);
    } else {
      setInputVal(String(page + 1));
    }
  }

  return (
    <div className="flex items-center justify-between text-sm text-muted-foreground select-none">
      <span className="tabular-nums text-xs">
        {formatInt(rowStart)}–{formatInt(rowEnd)} of {formatInt(total)}
      </span>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0"
          disabled={page === 0}
          onClick={() => onPage(0)}
          title="First page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0"
          disabled={page === 0}
          onClick={() => onPage(page - 1)}
          title="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-1 px-1 tabular-nums text-xs">
          <input
            ref={inputRef}
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onBlur={commitInput}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                commitInput();
                inputRef.current?.blur();
              }
            }}
            className={cn(
              "w-10 text-center rounded border border-border/60 bg-background",
              "text-xs h-6 px-1 focus:outline-none focus:ring-1 focus:ring-ring",
            )}
            aria-label="Page number"
          />
          <span className="text-muted-foreground/60">/ {formatInt(totalPages)}</span>
        </div>

        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0"
          disabled={page >= totalPages - 1}
          onClick={() => onPage(page + 1)}
          title="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0"
          disabled={page >= totalPages - 1}
          onClick={() => onPage(totalPages - 1)}
          title="Last page"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ── Column filter row cell components ─────────────────────────────────────
function TextFilter({
  value,
  onChange,
  placeholder = "filter…",
  hint,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: boolean;
}) {
  return (
    <div className="relative flex items-center">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-6 text-xs px-1.5 pr-6 bg-background/60 border-border/40"
      />
      {hint && (
        <span
          className="absolute right-1.5 text-[9px] text-muted-foreground/40 font-mono leading-none select-none"
          title="Supports /regex/ syntax"
        >
          .*
        </span>
      )}
    </div>
  );
}

function RangeFilter({
  minVal,
  maxVal,
  onMin,
  onMax,
  minPlaceholder = "min",
  maxPlaceholder = "max",
}: {
  minVal: string;
  maxVal: string;
  onMin: (v: string) => void;
  onMax: (v: string) => void;
  minPlaceholder?: string;
  maxPlaceholder?: string;
}) {
  return (
    <div className="flex gap-0.5">
      <Input
        value={minVal}
        onChange={(e) => onMin(e.target.value)}
        placeholder={minPlaceholder}
        className="h-6 w-[52px] text-xs px-1 bg-background/60 border-border/40 tabular-nums"
      />
      <Input
        value={maxVal}
        onChange={(e) => onMax(e.target.value)}
        placeholder={maxPlaceholder}
        className="h-6 w-[52px] text-xs px-1 bg-background/60 border-border/40 tabular-nums"
      />
    </div>
  );
}

// ── Sort indicator icon ────────────────────────────────────────────────────
function SortIcon({ colId, sort }: { colId: string; sort: string }) {
  if (!(colId in SORT_MAP)) return null;
  const [asc, desc] = SORT_MAP[colId];
  if (sort === asc && asc !== desc) return <ArrowUp className="h-3 w-3 inline ml-0.5 opacity-70" />;
  if (sort === desc) return <ArrowDown className="h-3 w-3 inline ml-0.5 opacity-70" />;
  return <ArrowUpDown className="h-3 w-3 inline ml-0.5 opacity-30" />;
}

// ── Main page ──────────────────────────────────────────────────────────────
export function ProductsPage() {
  const queryClient = useQueryClient();

  // ── global toolbar filters ───────────────────────────────────────────
  const [inStock, setInStock] = useState(false);
  const [hasMAP, setHasMAP] = useState(false);
  const [preset, setPreset] = useState<Preset | null>(null);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<SortKey>("qty_desc");

  // ── per-column filter state (debounced before sending to API) ────────
  const [colFilters, setColFilters] = useState<ColFilters>(EMPTY_FILTERS);
  const debouncedFilters = useDebounced(colFilters, 300);

  // Reset to page 0 whenever any filter changes
  const updateFilter = useCallback(
    <K extends keyof ColFilters>(key: K, val: ColFilters[K]) => {
      setColFilters((f) => ({ ...f, [key]: val }));
      setPage(0);
    },
    [],
  );

  // ── build API params ─────────────────────────────────────────────────
  const params = useMemo(
    () => ({
      sku_q: debouncedFilters.sku_q || undefined,
      mpn_q: debouncedFilters.mpn_q || undefined,
      desc_q: debouncedFilters.desc_q || undefined,
      brand: debouncedFilters.brand.length > 0 ? debouncedFilters.brand : undefined,
      unspsc_prefix: debouncedFilters.unspsc_prefix || undefined,
      curated_state:
        (debouncedFilters.curated_state as CuratedState | "uncurated" | undefined) ||
        undefined,
      min_cost: debouncedFilters.min_cost || undefined,
      max_cost: debouncedFilters.max_cost || undefined,
      min_map: debouncedFilters.min_map || undefined,
      max_map: debouncedFilters.max_map || undefined,
      min_margin: debouncedFilters.min_margin || undefined,
      max_margin: debouncedFilters.max_margin || undefined,
      min_qty: debouncedFilters.min_qty || undefined,
      max_qty: debouncedFilters.max_qty || undefined,
      in_stock: inStock || undefined,
      has_map: hasMAP || undefined,
      preset: preset ?? undefined,
      sort,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [debouncedFilters, inStock, hasMAP, preset, sort, page],
  );

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ["products", params],
    queryFn: () => api.products(params),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  // ── curated state mutation ───────────────────────────────────────────
  const { mutate: patchState } = useMutation({
    mutationFn: ({ id, state }: { id: string; state: CuratedState }) =>
      api.patchCuratedState(id, state),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });

  const handleStateChange = useCallback(
    (id: string, state: CuratedState) => patchState({ id, state }),
    [patchState],
  );

  // ── column sort cycling ──────────────────────────────────────────────
  const handleSortClick = useCallback(
    (colId: string) => {
      if (!(colId in SORT_MAP)) return;
      const [asc, desc] = SORT_MAP[colId];
      setSort((cur) => {
        if (cur === desc) return "qty_desc"; // cycle off → default
        if (cur === asc && asc !== desc) return desc;
        return asc;
      });
      setPage(0);
    },
    [],
  );

  // ── TanStack columns ─────────────────────────────────────────────────
  // We define columns here so they can close over handleStateChange.
  const columns = useMemo<ColumnDef<TdsynnexProduct>[]>(
    () => [
      {
        id: "sku",
        header: () => (
          <button
            className="flex items-center gap-0.5 uppercase text-[10px] font-medium tracking-wide"
            onClick={() => handleSortClick("sku")}
          >
            SKU <SortIcon colId="sku" sort={sort} />
          </button>
        ),
        cell: ({ row: { original: p } }) => (
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {p.tdsynnex_sku}
          </span>
        ),
        size: 110,
      },
      {
        id: "brand",
        header: () => (
          <span className="uppercase text-[10px] font-medium tracking-wide">Brand</span>
        ),
        cell: ({ row: { original: p } }) => (
          <Badge variant="outline" className="capitalize text-[10px] py-0">
            {p.brand_slug || "—"}
          </Badge>
        ),
        size: 90,
      },
      {
        id: "category",
        header: () => (
          <span className="uppercase text-[10px] font-medium tracking-wide">Category</span>
        ),
        cell: ({ row: { original: p } }) => (
          <span className="text-xs text-muted-foreground">
            {inferCategory(p.unspsc_code)}
          </span>
        ),
        size: 130,
      },
      {
        id: "description",
        header: () => (
          <span className="uppercase text-[10px] font-medium tracking-wide">Description</span>
        ),
        cell: ({ row: { original: p } }) => (
          <span className="text-xs" title={p.short_description}>
            {truncate(p.short_description, 72)}
          </span>
        ),
      },
      {
        id: "cost",
        header: () => (
          <button
            className="flex items-center gap-0.5 uppercase text-[10px] font-medium tracking-wide tabular-nums"
            onClick={() => handleSortClick("cost")}
          >
            Cost <SortIcon colId="cost" sort={sort} />
          </button>
        ),
        cell: ({ row: { original: p } }) => (
          <span className="tabular-nums font-mono text-xs">
            {p.contract_price ? formatUsd(parseFloat(p.contract_price)) : "—"}
          </span>
        ),
        size: 90,
      },
      {
        id: "map",
        header: () => (
          <button
            className="flex items-center gap-0.5 uppercase text-[10px] font-medium tracking-wide tabular-nums"
            onClick={() => handleSortClick("map")}
          >
            MAP <SortIcon colId="map" sort={sort} />
          </button>
        ),
        cell: ({ row: { original: p } }) => (
          <span className="tabular-nums font-mono text-xs">
            {p.map_price ? formatUsd(parseFloat(p.map_price)) : "—"}
          </span>
        ),
        size: 90,
      },
      {
        id: "margin",
        header: () => (
          <button
            className="flex items-center gap-0.5 uppercase text-[10px] font-medium tracking-wide tabular-nums"
            onClick={() => handleSortClick("margin")}
          >
            Margin <SortIcon colId="margin" sort={sort} />
          </button>
        ),
        cell: ({ row: { original: p } }) => {
          if (p.margin_pct == null)
            return <span className="text-muted-foreground text-xs">—</span>;
          return (
            <span
              className={cn(
                "tabular-nums font-mono text-xs font-medium",
                p.margin_pct >= 12 ? "text-emerald-400" : "text-amber-400",
              )}
            >
              {p.margin_pct.toFixed(1)}%
            </span>
          );
        },
        size: 80,
      },
      {
        id: "qty",
        header: () => (
          <button
            className="flex items-center gap-0.5 uppercase text-[10px] font-medium tracking-wide tabular-nums"
            onClick={() => handleSortClick("qty")}
          >
            Avail <SortIcon colId="qty" sort={sort} />
          </button>
        ),
        cell: ({ row: { original: p } }) => {
          const qty = p.qty_on_hand;
          const isDropship = p.sku_attributes?.[0] === 'Y';
          const isRefurb = p.sku_attributes?.[1] === 'Y';
          const qtyColor = cn(
            "tabular-nums text-xs font-mono",
            qty === 0 && "text-rose-400",
            qty > 0 && qty <= 5 && "text-amber-400",
            qty >= 9999 && "text-emerald-400",
            qty > 5 && qty < 9999 && "text-foreground",
          );
          const warehouseTitle = p.warehouse_qty && Object.keys(p.warehouse_qty).length > 0
            ? Object.entries(p.warehouse_qty).map(([k, v]) => `${k}: ${v}`).join(' | ')
            : undefined;
          return (
            <div className="flex flex-col gap-0.5">
              <span
                className={qtyColor}
                title={warehouseTitle}
              >
                {qty >= 9999 ? "Unlimited" : qty.toLocaleString()}
              </span>
              <div className="flex gap-1 flex-wrap">
                {isDropship && (
                  <span className="text-[9px] px-1 rounded bg-slate-700 text-slate-300">Drop-ship</span>
                )}
                {isRefurb && (
                  <span className="text-[9px] px-1 rounded bg-amber-900/60 text-amber-300">Refurb</span>
                )}
              </div>
            </div>
          );
        },
        size: 90,
      },
      {
        id: "curated",
        header: () => (
          <span className="uppercase text-[10px] font-medium tracking-wide">Curated</span>
        ),
        cell: ({ row: { original: p } }) => (
          <CuratedCell product={p} onStateChange={handleStateChange} />
        ),
        size: 180,
      },
    ],
    [sort, handleSortClick, handleStateChange],
  );

  // ── column filter row definitions ────────────────────────────────────
  // Keyed by column id — render order matches columns array above.
  const filterCells: Record<string, React.ReactNode> = {
    sku: (
      <TextFilter
        value={colFilters.sku_q}
        onChange={(v) => updateFilter("sku_q", v)}
        placeholder="filter…"
        hint
      />
    ),
    brand: (
      <BrandCombobox
        value={colFilters.brand}
        onChange={(v) => updateFilter("brand", v)}
      />
    ),
    category: (
      <select
        value={colFilters.unspsc_prefix}
        onChange={(e) => updateFilter("unspsc_prefix", e.target.value)}
        className={cn(
          "h-6 w-full rounded border border-border/40 bg-background/60",
          "text-xs px-1 text-foreground focus:outline-none focus:ring-1 focus:ring-ring",
        )}
      >
        <option value="">All</option>
        {UNSPSC_OPTIONS.map(({ prefix, label }) => (
          <option key={prefix} value={prefix}>
            {label}
          </option>
        ))}
      </select>
    ),
    description: (
      <TextFilter
        value={colFilters.desc_q}
        onChange={(v) => updateFilter("desc_q", v)}
        placeholder="filter…"
        hint
      />
    ),
    cost: (
      <RangeFilter
        minVal={colFilters.min_cost}
        maxVal={colFilters.max_cost}
        onMin={(v) => updateFilter("min_cost", v)}
        onMax={(v) => updateFilter("max_cost", v)}
      />
    ),
    map: (
      <RangeFilter
        minVal={colFilters.min_map}
        maxVal={colFilters.max_map}
        onMin={(v) => updateFilter("min_map", v)}
        onMax={(v) => updateFilter("max_map", v)}
      />
    ),
    margin: (
      <RangeFilter
        minVal={colFilters.min_margin}
        maxVal={colFilters.max_margin}
        onMin={(v) => updateFilter("min_margin", v)}
        onMax={(v) => updateFilter("max_margin", v)}
        minPlaceholder="min%"
        maxPlaceholder="max%"
      />
    ),
    qty: (
      <RangeFilter
        minVal={colFilters.min_qty}
        maxVal={colFilters.max_qty}
        onMin={(v) => updateFilter("min_qty", v)}
        onMax={(v) => updateFilter("max_qty", v)}
      />
    ),
    curated: (
      <select
        value={colFilters.curated_state}
        onChange={(e) => updateFilter("curated_state", e.target.value)}
        className={cn(
          "h-6 w-full rounded border border-border/40 bg-background/60",
          "text-xs px-1 text-foreground focus:outline-none focus:ring-1 focus:ring-ring",
        )}
      >
        <option value="">All</option>
        <option value="uncurated">Uncurated</option>
        <option value="draft">Draft / Out of stock</option>
        <option value="ready">Ready</option>
        <option value="published">Published</option>
        <option value="archived">Archived</option>
      </select>
    ),
  };

  // ── TanStack table instance (render-only) ────────────────────────────
  const table = useReactTable({
    data: data?.items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    pageCount: totalPages,
    state: {
      pagination: { pageIndex: page, pageSize: PAGE_SIZE },
      sorting: [] as SortingState,
    },
  });

  const togglePreset = (p: Preset) => {
    setPreset((cur) => (cur === p ? null : p));
    setPage(0);
  };

  const hasActiveFilters =
    Object.values(colFilters).some(Boolean) || inStock || hasMAP || preset;

  const clearAllFilters = () => {
    setColFilters(EMPTY_FILTERS);
    setInStock(false);
    setHasMAP(false);
    setPreset(null);
    setPage(0);
  };

  const paginationBar = totalPages > 1 && (
    <PaginationBar
      page={page}
      totalPages={totalPages}
      total={data?.total ?? 0}
      pageSize={PAGE_SIZE}
      onPage={setPage}
    />
  );

  return (
    <div className="flex flex-col gap-5">
      {/* ── Header ── */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Catalog</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            TD SYNNEX authorized SKUs — Dell · Lenovo · Samsung · Anker · APC
          </p>
        </div>
        <div className="flex items-center gap-3">
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
            >
              Clear filters
            </button>
          )}
          <span
            className={cn(
              "text-sm tabular-nums transition-opacity",
              isFetching ? "text-muted-foreground/50" : "text-muted-foreground",
            )}
          >
            {data ? `${formatInt(data.total)} SKUs` : "—"}
          </span>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="flex flex-col gap-3">
        {/* Preset pills + boolean toggles */}
        <div className="flex items-center gap-2 flex-wrap">
          <ListFilter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => togglePreset(p.id)}
              title={p.desc}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                preset === p.id
                  ? "bg-ember/20 border-ember/50 text-ember"
                  : "border-border/60 text-muted-foreground hover:border-border hover:text-foreground",
              )}
            >
              {p.icon}
              {p.label}
            </button>
          ))}
          <div className="h-4 w-px bg-border/60" />
          <button
            onClick={() => {
              setInStock((v) => !v);
              setPage(0);
            }}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors",
              inStock
                ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
                : "border-border/60 text-muted-foreground hover:border-border hover:text-foreground",
            )}
          >
            <PackageCheck className="h-3.5 w-3.5" />
            In stock
          </button>
          <button
            onClick={() => {
              setHasMAP((v) => !v);
              setPage(0);
            }}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors",
              hasMAP
                ? "bg-blue-500/20 border-blue-500/40 text-blue-400"
                : "border-border/60 text-muted-foreground hover:border-border hover:text-foreground",
            )}
          >
            MAP floor set
          </button>
        </div>
      </div>

      {/* ── Top pagination ── */}
      {paginationBar}

      {/* ── Table ── */}
      <div
        className={cn(
          "rounded-lg border border-border/60 overflow-hidden transition-opacity",
          isFetching && !isLoading && "opacity-70",
        )}
      >
        <Table>
          <TableHeader>
            {/* Header row */}
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="bg-muted/30 hover:bg-muted/30">
                {hg.headers.map((h) => (
                  <TableHead
                    key={h.id}
                    style={{ width: h.getSize() }}
                    className="py-2"
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}

            {/* Column filter row */}
            <TableRow className="bg-muted/10 hover:bg-muted/10 border-b border-border/40">
              {table.getHeaderGroups()[0]?.headers.map((h) => (
                <TableHead key={`filter-${h.id}`} className="py-1 px-2">
                  {filterCells[h.id] ?? null}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((col) => (
                    <TableCell key={String(col.id)}>
                      <div className="h-4 rounded shimmer" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center text-destructive py-16 text-sm"
                >
                  Failed to load catalog. Check sync-go is running.
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center text-muted-foreground py-16 text-sm"
                >
                  No SKUs match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="hover:bg-muted/20">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* ── Bottom pagination ── */}
      {paginationBar}
    </div>
  );
}
